import { DataGrid, GridColDef } from '@fashionnovateam/nova-ui-toolkit';
import classNames from 'classnames';
import { formatDate } from '../../utils';

type DueDateChangeProps = {
  newFobShipDate?: string;
  currentFobShipDate?: string;
  originalFobShipDate?: string;
  newDueDate: string;
  currentDueDate: string;
  originalDueDate: string;
  lateReason?: string;
  note?: string;
};

const columns: GridColDef[] = [
  {
    field: 'header',
    headerName: '',
    render: 'custom',
    flex: 1,
    sortable: false,
    minWidth: 48,
    disableColumnMenu: true,
  },
  {
    field: 'due_date',
    headerName: 'Due Date',
    render: 'custom',
    flex: 1,
    sortable: false,
    minWidth: 48,
    disableColumnMenu: true,
  },
  {
    field: 'due_date_diff',
    headerName: 'Day Diff',
    render: 'custom',
    flex: 1,
    sortable: false,
    minWidth: 150,
    disableColumnMenu: true,
  },
];

const columnsFca: GridColDef[] = [
  {
    field: 'header',
    headerName: '',
    render: 'custom',
    flex: 1,
    sortable: false,
    minWidth: 48,
    disableColumnMenu: true,
  },
  {
    field: 'fob_ship_date',
    headerName: 'FOB Ship Date',
    render: 'custom',
    flex: 1,
    sortable: false,
    minWidth: 48,
    disableColumnMenu: true,
  },
  {
    field: 'due_date_diff',
    headerName: 'Day Diff',
    render: 'custom',
    flex: 1,
    sortable: false,
    minWidth: 150,
    disableColumnMenu: true,
  },
];

function getDueDateDiff(dueDate: string, originalDueDate: string) {
  const dueDateDate = new Date(dueDate);
  const originalDueDateDate = new Date(originalDueDate);

  const diffTime =
    Date.UTC(
      dueDateDate.getUTCFullYear(),
      dueDateDate.getUTCMonth(),
      dueDateDate.getUTCDate(),
    ) -
    Date.UTC(
      originalDueDateDate.getUTCFullYear(),
      originalDueDateDate.getUTCMonth(),
      originalDueDateDate.getUTCDate(),
    );

  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
}

export function DueDateChange({
  newFobShipDate,
  currentFobShipDate,
  originalFobShipDate,
  newDueDate,
  currentDueDate,
  originalDueDate,
  lateReason,
  note,
}: DueDateChangeProps) {
  const transformedRows = [
    {
      id: 1,
      header: <span className="text-sm text-base-disabled">Requested</span>,
      fob_ship_date: (
        <span className="text-sm font-semibold text-warning-700">
          {newFobShipDate ? formatDate(newFobShipDate, 'numeric', 'UTC') : '-'}
        </span>
      ),
      due_date: (
        <span className="text-sm font-semibold text-warning-700">
          {newDueDate ? formatDate(newDueDate, 'numeric', 'UTC') : '-'}
        </span>
      ),
      due_date_diff: (
        <span className="text-sm text-base-primary">
          {!newFobShipDate || originalFobShipDate || currentFobShipDate
            ? getDueDateDiff(
                newFobShipDate || newDueDate,
                newFobShipDate
                  ? originalFobShipDate || currentFobShipDate || ''
                  : originalDueDate,
              )
            : '-'}
        </span>
      ),
    },
    {
      id: 2,
      header: <span className="text-sm text-base-disabled">Currently</span>,
      fob_ship_date: (
        <span
          className={classNames(
            'text-sm',
            newFobShipDate && currentFobShipDate
              ? 'text-base-disabled line-through decoration-base-disabled'
              : '',
          )}
        >
          {currentFobShipDate
            ? formatDate(currentFobShipDate, 'numeric', 'UTC')
            : '-'}
        </span>
      ),
      due_date: (
        <span
          className={classNames(
            'text-sm',
            newDueDate && currentDueDate
              ? 'text-base-disabled line-through decoration-base-disabled'
              : '',
          )}
        >
          {currentDueDate ? formatDate(currentDueDate, 'numeric', 'UTC') : '-'}
        </span>
      ),
      due_date_diff: (
        <span className="text-sm text-base-primary">
          {(newFobShipDate && currentFobShipDate && originalFobShipDate) ||
          (!newFobShipDate && currentDueDate && originalDueDate)
            ? getDueDateDiff(
                (newFobShipDate ? currentFobShipDate : currentDueDate) || '',
                (newFobShipDate ? originalFobShipDate : originalDueDate) || '',
              ) || '-'
            : '-'}
        </span>
      ),
    },
    ...((!newFobShipDate && originalDueDate) ||
    (newFobShipDate && originalFobShipDate)
      ? [
          {
            id: 3,
            header: (
              <span className="text-sm text-base-disabled">Original</span>
            ),
            fob_ship_date: (
              <span className="text-sm text-base-disabled">
                {originalFobShipDate
                  ? formatDate(originalFobShipDate, 'numeric', 'UTC')
                  : '-'}
              </span>
            ),
            due_date: (
              <span className="text-sm text-base-disabled">
                {formatDate(originalDueDate, 'numeric', 'UTC')}
              </span>
            ),
            due_date_diff: <span className="text-sm text-base-primary">-</span>,
          },
        ]
      : []),
  ];

  return (
    <div className="flex flex-col gap-3">
      <span className="text-xsm font-medium text-base-primary">
        {newFobShipDate ? 'FOB Ship Date' : 'Due Date'}
      </span>
      {lateReason && (
        <div className="flex flex-row gap-8">
          <span className="text-xsm text-base-disabled whitespace-nowrap">
            Reason
          </span>
          <span className="text-xsm text-base-primary">
            {lateReason}
            {note && (
              <>
                <br />
                {note}
              </>
            )}
          </span>
        </div>
      )}
      <DataGrid
        hideFilterButton
        hideGroupButton
        hideFooterPagination
        hideFooter
        hideSortButton
        rowHeight={32}
        rows={transformedRows}
        columns={newFobShipDate ? columnsFca : columns}
      />
    </div>
  );
}
